// HTTP Client - Generic fetch wrapper with error handling
import { getBaseUrl, getDefaultHeaders } from './apiConfig';

const REQUEST_TIMEOUT = 60000; // Increased to 60s for heavy dashboard data

/**
 * Custom error class for API errors
 */
export class ApiError extends Error {
  constructor(message, status, data = null) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.data = data;
  }
}

/**
 * Fetch with timeout utility
 */
const fetchWithTimeout = async (url, options, timeout = REQUEST_TIMEOUT) => {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeout);

  try {
    const response = await fetch(url, {
      ...options,
      signal: controller.signal
    });
    clearTimeout(timeoutId);
    return response;
  } catch (error) {
    clearTimeout(timeoutId);
    if (error.name === 'AbortError') {
      throw new ApiError('Request timeout', 408);
    }
    throw error;
  }
};

/**
 * Parse response based on content type
 */
const parseResponse = async (response) => {
  try {
    const text = await response.text();
    if (!text) return null;

    try {
      return JSON.parse(text);
    } catch (e) {
      // Not a JSON response, return as plain text
      return text;
    }
  } catch (error) {
    console.error('❌ Error parsing response:', error);
    return null;
  }
};

/**
 * Generic HTTP request handler
 */
const request = async (endpoint, options = {}) => {
  const baseUrl = getBaseUrl();
  const cleanEndpoint = (baseUrl.endsWith('/api') && endpoint.startsWith('/api/'))
    ? endpoint.substring(4)
    : endpoint;
  const url = `${baseUrl}${cleanEndpoint}`;

  const config = {
    ...options,
    headers: {
      ...getDefaultHeaders(options.token),
      ...options.headers
    }
  };

  // Remove token from config if passed (already added to headers)
  delete config.token;

  // For FormData bodies, remove Content-Type so the browser sets it with the multipart boundary
  if (config.body instanceof FormData) {
    delete config.headers['Content-Type'];
  }

  const requestTimeout = config._timeout || REQUEST_TIMEOUT;
  delete config._timeout;

  try {
    const response = await fetchWithTimeout(url, config, requestTimeout);
    const data = await parseResponse(response);

    if (!response.ok) {
      // Backend might wrap error in responseStatus.message
      const errorMessage = data?.responseStatus?.message || data?.message || `HTTP Error ${response.status}`;
      throw new ApiError(
        errorMessage,
        response.status,
        data
      );
    }

    // Check if response follows backend APIResponse structure
    // Backend returns: { responseStatus: {...}, responseData: [...] }
    if (data && typeof data === 'object' && 'responseStatus' in data && 'responseData' in data) {
      const { responseStatus, responseData } = data;

      // Check if the API call was successful (statusCode 0 means success)
      if (responseStatus.statusCode === 0) {
        return {
          success: true,
          data: responseData, // Return the actual data, not the wrapper
          status: response.status
        };
      } else {
        // Backend returned an error
        console.error('❌ Backend API Error:', {
          statusCode: responseStatus.statusCode,
          message: responseStatus.message,
          fullResponse: data
        });
        throw new ApiError(
          responseStatus.message || 'API Error',
          responseStatus.statusCode,
          data // Include full response for debugging
        );
      }
    }

    // Fallback for responses that don't follow APIResponse structure
    return {
      success: true,
      data,
      status: response.status
    };
  } catch (error) {
    if (error instanceof ApiError) {
      throw error;
    }

    // Network or other errors
    throw new ApiError(
      error.message || 'Network error occurred',
      0,
      null
    );
  }
};

/**
 * HTTP Methods
 */
export const httpClient = {
  get: (endpoint, options = {}) =>
    request(endpoint, { ...options, method: 'GET' }),

  post: (endpoint, data, options = {}) =>
    request(endpoint, {
      ...options,
      method: 'POST',
      body: data instanceof FormData ? data : JSON.stringify(data)
    }),

  put: (endpoint, data, options = {}) =>
    request(endpoint, {
      ...options,
      method: 'PUT',
      body: JSON.stringify(data)
    }),

  patch: (endpoint, data, options = {}) =>
    request(endpoint, {
      ...options,
      method: 'PATCH',
      body: JSON.stringify(data)
    }),

  delete: (endpoint, options = {}) =>
    request(endpoint, { ...options, method: 'DELETE' })
};

export default httpClient;

